// The Art of Revival - Saved Pieces
// Browser-local wishlist with defensive storage and shared update events.

class AORWishlist {
  constructor() {
    this.storageKey = 'aor_wishlist';
    this.toastTimer = null;
    this.handleWishlistClick = this.handleWishlistClick.bind(this);
    this.init();
  }

  init() {
    this.suppressShoppingPopup();
    this.ensureStyles();
    this.relocateCardHearts();
    this.updateHeartStates();

    document.addEventListener('click', this.handleWishlistClick, true);

    if (this.isFavoritesPage()) {
      this.renderFavoritesPage();
    }

    this.renderCartRecoveryPrompts();
    this.updateHeaderCount();
    this.renderSavedShortcut();

    document.addEventListener('aor:saved-pieces-updated', () => {
      this.updateHeartStates();
      this.updateHeaderCount();
      this.renderSavedShortcut();
    });
  }

  suppressShoppingPopup() {
    const shoppingPath = window.location.pathname === '/' ||
      /^\/(collections|products|search|cart)(\/|$)/.test(window.location.pathname);
    if (!shoppingPath) return;

    const hidePopups = () => {
      document.querySelectorAll('[data-aor-email-popup]').forEach((popup) => {
        if (popup.classList.contains('is-visible')) popup.classList.remove('is-visible');
        if (!popup.hidden) popup.hidden = true;
      });
    };

    hidePopups();

    if (!document.body || !window.MutationObserver) return;

    const observer = new MutationObserver(hidePopups);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'hidden']
    });
  }

  relocateCardHearts() {
    document.querySelectorAll('.card__media > .wishlist-heart-btn').forEach((button) => {
      const cardInner = button.closest('.card__inner');
      if (cardInner && button.parentElement !== cardInner) {
        cardInner.appendChild(button);
      }
      this.bindCardImageLink(cardInner);
    });
  }

  bindCardImageLink(cardInner) {
    if (!cardInner || cardInner.dataset.aorImageLinkBound === 'true') return;

    const productLink = cardInner.querySelector('.card__content a[href*="/products/"]');
    if (!productLink) return;

    cardInner.dataset.aorImageLinkBound = 'true';
    cardInner.addEventListener('click', (event) => {
      if (event.target.closest('.wishlist-heart-btn, a, button')) return;
      window.location.href = productLink.href;
    });
  }

  handleWishlistClick(event) {
    const heartBtn = event.target.closest('.wishlist-heart-btn');
    if (!heartBtn) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    this.toggleProduct(heartBtn);
  }

  readJSON(key, fallback) {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  writeJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Keep the interface usable if browser storage is unavailable.
    }
  }

  escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }

  isFavoritesPage() {
    return window.location.pathname.includes('/pages/favorites') ||
      window.location.pathname.includes('/pages/wishlist');
  }

  getWishlist() {
    return this.readJSON(this.storageKey, []);
  }

  saveWishlist(wishlist) {
    this.writeJSON(this.storageKey, wishlist);
    document.dispatchEvent(new CustomEvent('aor:saved-pieces-updated', {
      detail: { count: wishlist.length, items: wishlist }
    }));
  }

  isInWishlist(productId) {
    return this.getWishlist().some((item) => item.id === productId);
  }

  getProductData(heartBtn) {
    return {
      id: heartBtn.dataset.productId || heartBtn.dataset.productHandle,
      handle: heartBtn.dataset.productHandle,
      title: heartBtn.dataset.productTitle,
      price: heartBtn.dataset.productPrice,
      image: heartBtn.dataset.productImage,
      url: heartBtn.dataset.productUrl || `/products/${heartBtn.dataset.productHandle}`,
      addedAt: new Date().toISOString()
    };
  }

  toggleProduct(heartBtn) {
    const productData = this.getProductData(heartBtn);
    if (!productData.id) return;

    let wishlist = this.getWishlist();
    const existingIndex = wishlist.findIndex((item) => item.id === productData.id);

    if (existingIndex > -1) {
      wishlist.splice(existingIndex, 1);
      this.updateHeartButton(heartBtn, false);
      this.showSaveToast(productData, false);
    } else {
      wishlist.push(productData);
      this.updateHeartButton(heartBtn, true);
      this.showSaveToast(productData, true);
    }

    this.saveWishlist(wishlist);
    this.updateHeaderCount();

    if (this.isFavoritesPage()) {
      this.renderFavoritesPage();
    }
  }

  updateHeartButton(button, isActive) {
    const outline = button.querySelector('.heart-outline');
    const fill = button.querySelector('.heart-fill');

    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-label', isActive ? 'Remove from saved pieces' : 'Save piece');
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute('title', isActive ? 'Saved' : 'Save piece');

    if (outline) outline.style.display = isActive ? 'none' : 'block';
    if (fill) fill.style.display = isActive ? 'block' : 'none';
  }

  updateHeartStates() {
    document.querySelectorAll('.wishlist-heart-btn').forEach((btn) => {
      this.updateHeartButton(btn, this.isInWishlist(btn.dataset.productId || btn.dataset.productHandle));
    });
  }

  updateHeaderCount() {
    const count = this.getWishlist().length;

    document.querySelectorAll('.wishlist-count, [data-saved-count]').forEach((countBadge) => {
      countBadge.textContent = count;
      countBadge.style.display = count > 0 ? 'block' : 'none';
    });
  }

  getToast() {
    let toast = document.querySelector('[data-aor-save-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'aor-save-toast';
    toast.setAttribute('data-aor-save-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
    return toast;
  }

  showSaveToast(product, wasSaved) {
    const toast = this.getToast();
    const title = this.escapeHTML(product.title || 'This piece');
    const message = wasSaved ? 'Saved to your pieces' : 'Removed from saved pieces';

    window.clearTimeout(this.toastTimer);
    toast.innerHTML = `
      <div class="aor-save-toast__message">
        <strong>${message}</strong>
        <span>${title}</span>
      </div>
      <div class="aor-save-toast__actions">
        <a href="/pages/favorites">View saved</a>
        <button type="button" data-aor-save-toast-close>Keep shopping</button>
      </div>
    `;
    toast.classList.add('is-visible');

    const closeButton = toast.querySelector('[data-aor-save-toast-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => toast.classList.remove('is-visible'), { once: true });
    }

    this.toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, wasSaved ? 4200 : 2600);
  }

  renderSavedShortcut() {
    const wishlist = this.getWishlist();
    const count = wishlist.length;
    let shortcut = document.querySelector('[data-aor-saved-shortcut]');

    if (count === 0) {
      if (shortcut) shortcut.remove();
      return;
    }

    if (!shortcut) {
      shortcut = document.createElement('a');
      shortcut.className = 'aor-saved-shortcut';
      shortcut.setAttribute('data-aor-saved-shortcut', '');
      shortcut.href = '/pages/favorites';
      document.body.appendChild(shortcut);
    }

    shortcut.setAttribute('aria-label', `View ${count} saved ${count === 1 ? 'piece' : 'pieces'}`);
    shortcut.innerHTML = `
      <span class="aor-saved-shortcut__heart" aria-hidden="true">♡</span>
      <span>${count} saved ${count === 1 ? 'piece' : 'pieces'}</span>
    `;
  }

  formatPrice(price) {
    const rawPrice = String(price || '').trim();
    if (!rawPrice) return '';
    if (rawPrice.includes('$')) return rawPrice;

    return `$${(parseInt(rawPrice, 10) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  renderFavoritesPage() {
    const container = document.querySelector('[data-wishlist-container]');
    if (!container) return;

    const wishlist = this.getWishlist();

    if (wishlist.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty">
          <h2>No saved pieces yet</h2>
          <p>Tap the heart on any piece you want to compare, revisit, or ask Emily about.</p>
          <div class="wishlist-actions">
            <a href="/collections/all?sort_by=created-descending" class="btn-browse-collection">Shop new arrivals</a>
            <a href="/collections/all" class="underlined-link">Browse all pieces</a>
          </div>
        </div>
      `;
      return;
    }

    const productsHTML = wishlist.map((product) => {
      const title = this.escapeHTML(product.title);
      const url = this.escapeHTML(product.url);
      const image = this.escapeHTML(product.image);
      const price = this.escapeHTML(this.formatPrice(product.price));

      return `
        <div class="wishlist-product-card">
          <div class="wishlist-product-image">
            <a href="${url}">
              ${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ''}
            </a>
            <button
              class="wishlist-heart-btn is-active"
              data-product-id="${this.escapeHTML(product.id)}"
              data-product-handle="${this.escapeHTML(product.handle)}"
              data-product-title="${title}"
              data-product-price="${this.escapeHTML(product.price)}"
              data-product-image="${image}"
              data-product-url="${url}"
              aria-label="Remove from saved pieces"
              aria-pressed="true"
              type="button"
            >
              <svg class="wishlist-heart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="heart-outline" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: none;"/>
                <path class="heart-fill" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div class="wishlist-product-info">
            <a href="${url}" class="wishlist-product-title">${title}</a>
            ${price ? `<p class="wishlist-product-price">${price}</p>` : ''}
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="wishlist-header">
        <div>
          <h1>Your Saved Pieces</h1>
          <p class="wishlist-count-text">${wishlist.length} ${wishlist.length === 1 ? 'piece' : 'pieces'} saved</p>
        </div>
        <div class="wishlist-actions">
          <a href="/collections/all?sort_by=created-descending" class="underlined-link">Keep shopping</a>
          <a href="/pages/contact" class="underlined-link">Ask Emily</a>
        </div>
      </div>
      <div class="wishlist-grid">
        ${productsHTML}
      </div>
    `;
  }

  renderCartRecoveryPrompts() {
    if (!window.location.pathname.startsWith('/cart')) return;

    const savedCount = this.getWishlist().length;
    const browseHref = '/collections/all?sort_by=created-descending';
    const savedLink = savedCount > 0
      ? `<a href="/pages/favorites" class="underlined-link">Return to ${savedCount} saved ${savedCount === 1 ? 'piece' : 'pieces'}</a>`
      : `<a href="/pages/favorites" class="underlined-link">View saved pieces</a>`;

    const hasCartItems = !document.querySelector('cart-items.is-empty, #main-cart-footer.is-empty');
    const filledCartTarget = hasCartItems ? document.querySelector('.cart__ctas') : null;
    if (filledCartTarget && !document.querySelector('[data-aor-cart-assist]')) {
      filledCartTarget.insertAdjacentHTML('afterend', `
        <div class="aor-cart-assist" data-aor-cart-assist style="margin-top:16px;font-size:14px;line-height:1.55;color:rgba(44,36,22,.72);">
          <p style="margin:0 0 8px;">Need delivery timing, dimensions, or a quick hold before checkout?</p>
          <div style="display:flex;flex-wrap:wrap;gap:10px 16px;">
            <a href="/pages/contact" class="underlined-link">Ask Emily</a>
            <a href="${browseHref}" class="underlined-link">Keep shopping</a>
            ${savedLink}
          </div>
        </div>
      `);
    }

    const emptyCartTarget = hasCartItems ? null : document.querySelector('.cart__warnings');
    if (emptyCartTarget && !emptyCartTarget.querySelector('[data-aor-empty-cart-assist]')) {
      emptyCartTarget.insertAdjacentHTML('beforeend', `
        <div class="aor-empty-cart-assist" data-aor-empty-cart-assist style="margin-top:20px;display:flex;flex-wrap:wrap;justify-content:center;gap:10px 18px;font-size:14px;">
          <a href="${browseHref}" class="underlined-link">Browse new arrivals</a>
          ${savedLink}
          <a href="/pages/contact" class="underlined-link">Ask for sourcing help</a>
        </div>
      `);
    }
  }

  ensureStyles() {
    if (document.querySelector('[data-aor-wishlist-styles]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-aor-wishlist-styles', '');
    style.textContent = `
      .wishlist-heart-btn {
        align-items: center;
        background: rgba(250, 246, 240, 0.92);
        border: 1px solid rgba(44, 36, 22, 0.14);
        border-radius: 999px;
        box-shadow: 0 8px 22px rgba(44, 36, 22, 0.12);
        color: #2c2416;
        cursor: pointer;
        display: inline-flex;
        height: 44px;
        justify-content: center;
        min-height: 44px;
        min-width: 44px;
        padding: 0;
        transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
        width: 44px;
        z-index: 5;
      }

      .card__media,
      .card__inner,
      .wishlist-product-image {
        position: relative;
      }

      .card__inner > .wishlist-heart-btn,
      .card__media .wishlist-heart-btn,
      .wishlist-product-image .wishlist-heart-btn {
        pointer-events: auto;
        position: absolute;
        right: 12px;
        top: 12px;
        z-index: 50;
      }

      .card__inner:has(> .wishlist-heart-btn) {
        z-index: 2;
      }

      .card__inner > .card__content {
        pointer-events: none;
      }

      .wishlist-heart-btn:hover,
      .wishlist-heart-btn:focus-visible {
        background: #fff;
        color: #c85b4f;
        transform: translateY(-1px);
      }

      .wishlist-heart-btn.is-active {
        background: #c85b4f;
        border-color: #c85b4f;
        color: #fff;
      }

      .wishlist-heart-icon {
        height: 22px;
        pointer-events: none;
        width: 22px;
      }

      .aor-save-toast,
      .aor-saved-shortcut {
        font-family: var(--font-body-family, inherit);
      }

      .aor-save-toast {
        align-items: center;
        background: #faf6f0;
        border: 1px solid rgba(44, 36, 22, 0.14);
        box-shadow: 0 18px 48px rgba(44, 36, 22, 0.18);
        color: #2c2416;
        display: flex;
        gap: 18px;
        justify-content: space-between;
        left: 50%;
        max-width: min(560px, calc(100vw - 32px));
        opacity: 0;
        padding: 14px 16px;
        pointer-events: none;
        position: fixed;
        top: 18px;
        transform: translate(-50%, -14px);
        transition: opacity 180ms ease, transform 180ms ease;
        width: max-content;
        z-index: 2147483000;
      }

      .aor-save-toast.is-visible {
        opacity: 1;
        pointer-events: auto;
        transform: translate(-50%, 0);
      }

      .aor-save-toast__message {
        display: grid;
        gap: 2px;
        min-width: 0;
      }

      .aor-save-toast__message strong {
        font-size: 14px;
        line-height: 1.25;
      }

      .aor-save-toast__message span {
        color: rgba(44, 36, 22, 0.68);
        font-size: 12px;
        line-height: 1.35;
        max-width: 280px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .aor-save-toast__actions {
        align-items: center;
        display: flex;
        flex: 0 0 auto;
        gap: 12px;
      }

      .aor-save-toast__actions a,
      .aor-save-toast__actions button,
      .wishlist-actions a {
        color: #8f463b;
        font: inherit;
        font-size: 13px;
        text-decoration: underline;
        text-underline-offset: 0.22em;
      }

      .aor-save-toast__actions button {
        background: transparent;
        border: 0;
        cursor: pointer;
        padding: 0;
      }

      .aor-saved-shortcut {
        align-items: center;
        background: #2c2416;
        border: 1px solid rgba(250, 246, 240, 0.18);
        border-radius: 999px;
        bottom: 18px;
        box-shadow: 0 14px 34px rgba(44, 36, 22, 0.22);
        color: #faf6f0;
        display: inline-flex;
        font-size: 14px;
        gap: 8px;
        left: 18px;
        line-height: 1;
        min-height: 44px;
        padding: 0 16px;
        position: fixed;
        text-decoration: none;
        z-index: 20;
      }

      .aor-saved-shortcut:hover {
        color: #faf6f0;
        transform: translateY(-1px);
      }

      .aor-saved-shortcut__heart {
        color: #d8705b;
        font-size: 18px;
        line-height: 1;
      }

      .wishlist-header {
        align-items: end;
        display: flex;
        gap: 18px;
        justify-content: space-between;
      }

      .wishlist-actions {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 10px 18px;
      }

      @media screen and (max-width: 749px) {
        .wishlist-heart-btn {
          height: 42px;
          min-height: 42px;
          min-width: 42px;
          width: 42px;
        }

        .aor-save-toast {
          align-items: stretch;
          bottom: 82px;
          flex-direction: column;
          gap: 10px;
          left: 12px;
          max-width: none;
          padding: 13px 14px;
          right: 12px;
          top: auto;
          transform: translateY(16px);
          width: auto;
        }

        .aor-save-toast.is-visible {
          transform: translateY(0);
        }

        .aor-save-toast__message span {
          max-width: 100%;
        }

        .aor-save-toast__actions {
          justify-content: space-between;
        }

        .aor-saved-shortcut {
          bottom: 18px;
          left: 12px;
          max-width: calc(100vw - 112px);
          min-height: 42px;
        }

        .wishlist-header {
          align-items: flex-start;
          flex-direction: column;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .wishlist-heart-btn,
        .aor-save-toast,
        .aor-saved-shortcut {
          transition: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.aorWishlist = new AORWishlist();
  });
} else {
  window.aorWishlist = new AORWishlist();
}
